import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Save,
  X
} from 'lucide-react';
import { useNotify } from 'react-admin';
import {
  createNcrTemplate,
  getNcrTemplateById,
  updateNcrTemplate,
  type NcrTemplate,
  type NcrTemplateProperty,
  type NcrTemplateStage
} from '../../../services/ncrTemplateService.ts';
import { getAllUsers, type User } from '../../../services/userService.ts';
import './NcrTemplateEdit.css';

const NcrTemplateEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const isCreate = !id;
  const isViewMode = location.pathname.endsWith('/view');

  const [template, setTemplate] = useState<NcrTemplate>({
    name: '',
    description: '',
    isActive: true,
    stages: [],
    properties: []
  });

  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(!isCreate);
  const [users, setUsers] = useState<User[]>([]);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getNcrTemplateById(id);
      setTemplate(data);
    } catch (error) {
      notify('Failed to load template', { type: 'warning' });
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    if (!isCreate && id) {
      fetchTemplate();
    }
  }, [id, isCreate, fetchTemplate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList = await getAllUsers();
        setUsers(userList);
      } catch (error) {
        console.error('Failed to load users:', error);
        notify('Failed to load users', { type: 'warning' });
      }
    };

    fetchUsers();
  }, [notify]);

  const handleSave = async () => {
    if (isViewMode) return;
    try {
      await (isCreate
        ? await createNcrTemplate(template)
        : await updateNcrTemplate(id!, template));

      notify(isCreate ? 'Template created successfully' : 'Template updated successfully');
      navigate('/NcrTemplate');
    } catch (error) {
      notify('Failed to save template', { type: 'warning' });
      console.error('Error saving template:', error);
    }
  };

  const addStage = () => {
    const newStage: NcrTemplateStage = {
      name: `Stage ${template.stages.length + 1}`,
      description: '',
      sortOrder: template.stages.length,
      children: []
    };
    setTemplate({
      ...template,
      stages: [...template.stages, newStage]
    });
  };

  const updateStage = (index: number, updates: Partial<NcrTemplateStage>) => {
    const newStages = [...template.stages];
    newStages[index] = { ...newStages[index], ...updates };
    setTemplate({ ...template, stages: newStages });
  };

  const deleteStage = (index: number) => {
    const newStages = template.stages.filter((_, i) => i !== index);
    setTemplate({ ...template, stages: newStages });
  };

  const moveStageUp = (index: number) => {
    if (index === 0) return;
    const newStages = [...template.stages];
    [newStages[index], newStages[index - 1]] = [newStages[index - 1], newStages[index]];
    newStages.forEach((stage, i) => (stage.sortOrder = i));
    setTemplate({ ...template, stages: newStages });
  };

  const moveStageDown = (index: number) => {
    if (index === template.stages.length - 1) return;
    const newStages = [...template.stages];
    [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
    newStages.forEach((stage, i) => (stage.sortOrder = i));
    setTemplate({ ...template, stages: newStages });
  };

  const toggleStageExpanded = (index: number) => {
    const newSet = new Set(expandedStages);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setExpandedStages(newSet);
  };

  const addProperty = () => {
    const newProperty: NcrTemplateProperty = {
      name: `Property ${template.properties.length + 1}`,
      description: '',
      isRequired: false,
      sortOrder: template.properties.length
    };
    setTemplate({
      ...template,
      properties: [...template.properties, newProperty]
    });
  };

  const updateProperty = (index: number, updates: Partial<NcrTemplateProperty>) => {
    const newProperties = [...template.properties];
    newProperties[index] = { ...newProperties[index], ...updates };
    setTemplate({ ...template, properties: newProperties });
  };

  const deleteProperty = (index: number) => {
    const newProperties = template.properties.filter((_, i) => i !== index);
    setTemplate({ ...template, properties: newProperties });
  };

  const movePropertyUp = (index: number) => {
    if (index === 0) return;
    const newProperties = [...template.properties];
    [newProperties[index], newProperties[index - 1]] = [newProperties[index - 1], newProperties[index]];
    newProperties.forEach((prop, i) => (prop.sortOrder = i));
    setTemplate({ ...template, properties: newProperties });
  };

  const movePropertyDown = (index: number) => {
    if (index === template.properties.length - 1) return;
    const newProperties = [...template.properties];
    [newProperties[index], newProperties[index + 1]] = [newProperties[index + 1], newProperties[index]];
    newProperties.forEach((prop, i) => (prop.sortOrder = i));
    setTemplate({ ...template, properties: newProperties });
  };

  if (loading) {
    return <div className="ncr-loading">Loading...</div>;
  }

  return (
    <div className="ncr-template-edit-container">
      <div className="ncr-edit-header">
        <h1>{isViewMode ? 'View NCR Template' : (isCreate ? 'Create NCR Template' : 'Edit NCR Template')}</h1>
        <button className="ncr-btn ncr-btn-close" onClick={() => navigate('/NcrTemplate')}>
          <X size={20} />
        </button>
      </div>

      <div className="ncr-edit-content">
        {/* Template Info Section */}
        <div className="ncr-section">
          <h2>Template Information</h2>
          <div className="ncr-form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={template.name}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              placeholder="Enter template name"
              className="ncr-input"
              disabled={isViewMode}
            />
          </div>

          <div className="ncr-form-group">
            <label>Description</label>
            <textarea
              value={template.description || ''}
              onChange={(e) => setTemplate({ ...template, description: e.target.value })}
              placeholder="Enter template description"
              className="ncr-textarea"
              rows={3}
              disabled={isViewMode}
            />
          </div>

        </div>

        {/* Stages Section */}
        <div className="ncr-section">
          <div className="ncr-section-header">
            <h2>Stages</h2>
            {!isViewMode && (
              <button className="ncr-btn ncr-btn-secondary" onClick={addStage}>
                <Plus size={18} /> Add Stage
              </button>
            )}
          </div>

          <div className="ncr-stages-list">
            {template.stages.length === 0 ? (
              <div className="ncr-empty-message">No stages defined. Click "Add Stage" to create one.</div>
            ) : (
              template.stages.map((stage, index) => (
                <div key={index} className="ncr-stage-item">
                  <div className="ncr-stage-header" onClick={() => toggleStageExpanded(index)}>
                    <div className="ncr-stage-title">
                      {expandedStages.has(index) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      <span className="ncr-stage-number">Stage {index + 1}</span>
                      <span className="ncr-stage-name">{stage.name}</span>
                    </div>
                    <div className="ncr-stage-actions">
                      {!isViewMode && (
                        <>
                          <button
                            className="ncr-btn-icon ncr-btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveStageUp(index);
                            }}
                            disabled={index === 0}
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            className="ncr-btn-icon ncr-btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveStageDown(index);
                            }}
                            disabled={index === template.stages.length - 1}
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            className="ncr-btn-icon ncr-btn-delete ncr-btn-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteStage(index);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {expandedStages.has(index) && (
                    <div className="ncr-stage-details">
                      <div className="ncr-form-group">
                        <label>Stage Name *</label>
                        <input
                          type="text"
                          value={stage.name}
                          onChange={(e) => updateStage(index, { name: e.target.value })}
                          className="ncr-input"
                          disabled={isViewMode}
                        />
                      </div>

                      <div className="ncr-form-group">
                        <label>Description</label>
                        <textarea
                          value={stage.description || ''}
                          onChange={(e) => updateStage(index, { description: e.target.value })}
                          className="ncr-textarea"
                          rows={3}
                          disabled={isViewMode}
                        />
                      </div>

                      <div className="ncr-form-group">
                        <label>Owner (Optional)</label>
                        <Autocomplete
                          options={users}
                          value={users.find((u) => u.id === stage.ownerId) || null}
                          onChange={(_, value) => updateStage(index, { ownerId: value?.id })}
                          getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          noOptionsText="No users found"
                          disabled={isViewMode}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search and select owner"
                              size="small"
                            />
                          )}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              minHeight: 42,
                              borderRadius: '0.5rem',
                            },
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Properties Section */}
        <div className="ncr-section">
          <div className="ncr-section-header">
            <h2>Properties (Attachments)</h2>
            {!isViewMode && (
              <button className="ncr-btn ncr-btn-secondary" onClick={addProperty}>
                <Plus size={18} /> Add Property
              </button>
            )}
          </div>

          <div className="ncr-properties-list">
            {template.properties.length === 0 ? (
              <div className="ncr-empty-message">No properties defined. Click "Add Property" to create one.</div>
            ) : (
              <div className="ncr-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Required</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.properties.map((property, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={property.name}
                            onChange={(e) => updateProperty(index, { name: e.target.value })}
                            className="ncr-input-inline"
                            disabled={isViewMode}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={property.description || ''}
                            onChange={(e) => updateProperty(index, { description: e.target.value })}
                            className="ncr-input-inline"
                            disabled={isViewMode}
                          />
                        </td>
                        <td className="ncr-center">
                          <input
                            type="checkbox"
                            checked={property.isRequired}
                            onChange={(e) => updateProperty(index, { isRequired: e.target.checked })}
                            disabled={isViewMode}
                          />
                        </td>
                        <td className="ncr-actions">
                          {!isViewMode && (
                            <>
                              <button
                                className="ncr-btn-icon ncr-btn-small"
                                onClick={() => movePropertyUp(index)}
                                disabled={index === 0}
                                title="Move up"
                              >
                                <ChevronUp size={16} />
                              </button>
                              <button
                                className="ncr-btn-icon ncr-btn-small"
                                onClick={() => movePropertyDown(index)}
                                disabled={index === template.properties.length - 1}
                                title="Move down"
                              >
                                <ChevronDown size={16} />
                              </button>
                              <button
                                className="ncr-btn-icon ncr-btn-delete ncr-btn-small"
                                onClick={() => deleteProperty(index)}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="ncr-edit-footer">
        {!isViewMode && (
          <button className="ncr-btn ncr-btn-primary" onClick={handleSave}>
            <Save size={20} />
            {isCreate ? 'Create Template' : 'Save Changes'}
          </button>
        )}
        <button className="ncr-btn ncr-btn-secondary" onClick={() => navigate('/NcrTemplate')}>
          {isViewMode ? 'Back to List' : 'Cancel'}
        </button>
      </div>
    </div>
  );
};

export default NcrTemplateEdit;
